/**
 * Minimal `vuex-class`-compatible decorators for Vue 3 + vue-facing-decorator.
 *
 * vuex-class has no Vue 3 port. This shim reproduces the exact subset the IRIS
 * codebase uses so migration only requires changing the import path from
 * 'vuex-class' to '@/plugins/vuex-decorators'.
 *
 * Supported forms (all present in the codebase):
 *   @Getter('name')                       -> this.$store.getters['name']
 *   @Getter('name', { namespace })        -> this.$store.getters['ns/name']
 *   @Action('name', { namespace })        -> (payload) => this.$store.dispatch('ns/name', payload)
 *   @Mutation('name', { namespace })      -> (payload) => this.$store.commit('ns/name', payload)
 *
 * It works by defining a getter (for @Getter) or a method (for @Action /
 * @Mutation) directly on the class prototype. vue-facing-decorator picks up
 * prototype getters as computed and prototype functions as methods, and inside
 * them `this` is the component instance, so `this.$store` is available.
 */

interface BindingOptions {
    namespace?: string;
}

function resolveKey(type: string, options?: BindingOptions): string {
    return options && options.namespace ? `${options.namespace}/${type}` : type;
}

export function Getter(type: string, options?: BindingOptions) {
    const key = resolveKey(type, options);
    return function (target: any, propertyKey: string) {
        Object.defineProperty(target, propertyKey, {
            get(this: any) {
                return this.$store.getters[key];
            },
            enumerable: true,
            configurable: true,
        });
    };
}

export function Action(type: string, options?: BindingOptions) {
    const key = resolveKey(type, options);
    return function (target: any, propertyKey: string) {
        Object.defineProperty(target, propertyKey, {
            value(this: any, payload?: any) {
                return this.$store.dispatch(key, payload);
            },
            enumerable: false,
            writable: true,
            configurable: true,
        });
    };
}

export function Mutation(type: string, options?: BindingOptions) {
    const key = resolveKey(type, options);
    return function (target: any, propertyKey: string) {
        Object.defineProperty(target, propertyKey, {
            value(this: any, payload?: any) {
                return this.$store.commit(key, payload);
            },
            enumerable: false,
            writable: true,
            configurable: true,
        });
    };
}
